import React from "react";
import { Loader2 } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PropertyVisualizeStats } from "@/components/property-visualize/property-visualize-stats";
import { PropertyVisualizeBreakdownChart } from "@/components/property-visualize/property-visualize-breakdown-chart";
import { FeatureChart } from "@/components/feature-chart";
import { RoomVisualizerSDK } from "@/components/room-visualizer/room-visualizer-sdk";
import { Activities } from "@/components/activities";
import { RoomVisualizeHistory } from "@/components/room-visualize/room-visualize-history";
import { GenerationsList } from "@/components/generations-list";
import { CatalogueSelector } from "@/components/catalogue-selector";
import { useCatalogueSelection } from "@/hooks/use-catalogue-selection";

import { RoomVisualizeSDK } from "@/components/room-visualize/room-visualize-sdk";
import { RoomVisualizerStats } from "@/components/room-visualizer/room-visualizer-stats";
import { getConfig } from "@/config";

const RoomVisualizerPage: React.FC = () => {
  const {
    isLoading: areCataloguesLoading, isError: catalogueError,
    selectedCatalogueId, setSelectedCatalogueId, activeCatalogueId,
  } = useCatalogueSelection();
  const sdkUrl = getConfig().VITE_ROOM_VISUALIZE_SDK_URL;
  const useNewVisualizer = Boolean(sdkUrl);

  return (
    <>
      <main className="flex-1 space-y-6 p-2">
        <div className="space-y-2">
          <h2 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">
            Room Visualizer
          </h2>
          <p className="text-muted-foreground">
            Monitor room visualization performance and analytics
          </p>
        </div>

        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="generations">Generations</TabsTrigger>
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
            <TabsTrigger value="playground">Playground</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            {useNewVisualizer ? <RoomVisualizerStats /> : <PropertyVisualizeStats />}

            <Card>
              <CardHeader>
                <CardTitle>Room Visualization Trends</CardTitle>
                <CardDescription>Visualizations created over time</CardDescription>
              </CardHeader>
              <CardContent>
                <FeatureChart
                  featureKey={useNewVisualizer ? "roomVisualizer" : "propertyVisualize"}
                  usersKey={useNewVisualizer ? "roomVisualizerUsers" : "propertyVisualizeUsers"}
                  dataLabel="Visualizations"
                  color="#9D4EDD"
                  showUsers={true}
                />
              </CardContent>
            </Card>

            {!useNewVisualizer && <Card>
              <CardHeader>
                <CardTitle>Breakdown by Type</CardTitle>
                <CardDescription>
                  Usage split across visualization types
                </CardDescription>
              </CardHeader>
              <CardContent>
                <PropertyVisualizeBreakdownChart />
              </CardContent>
            </Card>}
          </TabsContent>

          <TabsContent value="generations" className="space-y-6">
            {useNewVisualizer ? <RoomVisualizeHistory sdkUrl={sdkUrl} /> : <GenerationsList type="property-visualize" />}
          </TabsContent>

          <TabsContent value="analytics" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Room Visualization Trends</CardTitle>
                <CardDescription>Visualizations created over time</CardDescription>
              </CardHeader>
              <CardContent>
                <FeatureChart
                  featureKey={useNewVisualizer ? "roomVisualizer" : "propertyVisualize"}
                  usersKey={useNewVisualizer ? "roomVisualizerUsers" : "propertyVisualizeUsers"}
                  dataLabel="Visualizations"
                  color="#9D4EDD"
                  showUsers={true}
                />
              </CardContent>
            </Card>

            <Activities
              filterEventType={useNewVisualizer ? "ROOM_VISUALIZE" : "PROPERTY_VISUALIZE"}
              title="Room Visualizer Analytics"
              description="Event history and analytics"
            />
          </TabsContent>

          <TabsContent value="playground" className="space-y-6">
            <div className="w-full mx-auto">
              <Card>
                <CardHeader className="flex flex-row items-center justify-end space-y-0 pb-2">
                  <CatalogueSelector value={selectedCatalogueId} onValueChange={setSelectedCatalogueId} />
                </CardHeader>
                <CardContent>
                  {areCataloguesLoading ? (
                    <div className="flex justify-center items-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    </div>
                  ) : activeCatalogueId ? (
                    useNewVisualizer ? (
                      <RoomVisualizeSDK catalogueId={activeCatalogueId} sdkUrl={sdkUrl} />
                    ) : <RoomVisualizerSDK catalogueId={activeCatalogueId} />
                  ) : (
                    <div className="flex items-center justify-center py-12 text-center">
                      <p className="text-muted-foreground max-w-[400px]">
                        {catalogueError ? "Catalogues are temporarily unavailable." : "Create a catalogue before using the playground."}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </>
  );
};

export default RoomVisualizerPage;
